import CancelIcon from "@mui/icons-material/Cancel";
import {
  Box,
  FormControl,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import Education from "../../../../types/Education";
import { gray } from "../../../shared-theme/themePrimitives";
import { degreeOptions } from "../../../utils/data";
import { EducationDateRangePicker } from "./components/EducationDateRangePicker";

interface EducationSectionProps {
  index: number;
  education: Education;
  handleEducation: (e: any, index: number) => void;
  removeEducation: (index: number) => void;
  errors: Record<string, string>;
  removeErrorMessage: (field: string) => void;
}

export const EducationSection: React.FC<EducationSectionProps> = ({
  index,
  education,
  handleEducation,
  removeEducation,
  errors,
  removeErrorMessage,
}) => {
  const [hoveredSection, setHoveredSection] = useState<number | null>(null);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gap: "2rem",
        alignItems: "center",
        position: "relative",
        border:
          index > 0 && hoveredSection === index ? `1px solid ${gray[400]}` : "",
        borderRadius: index > 0 && hoveredSection === index ? "8px" : "",
        padding: "0.5rem",
        marginTop: index > 0 ? "1rem" : "",
      }}
      onMouseEnter={() => setHoveredSection(index)}
      onMouseLeave={() => setHoveredSection(null)}
    >
      {index > 0 && hoveredSection === index && (
        <IconButton
          onClick={() => removeEducation(index)}
          sx={{
            position: "absolute",
            top: -10,
            right: -10,
            border: "none",
            backgroundColor: gray[100],
            "&:hover": {
              backgroundColor: gray[100],
            },
          }}
        >
          <CancelIcon />
        </IconButton>
      )}
      <FormControl
        sx={{
          marginBottom:
            !errors[`educations[${index}].degree`] &&
            (errors[`educations[${index}].institution`] ||
              errors[`educations[${index}].period`])
              ? "20px"
              : "",
        }}
      >
        <FormLabel htmlFor="degree">Degree</FormLabel>
        <Select
          id={`degree-${index}`}
          name="degree"
          value={education.degree || ""}
          error={!!errors[`educations[${index}].degree`]}
          displayEmpty
          renderValue={(selected) =>
            selected ? (
              selected
            ) : (
              <span style={{ opacity: 0.6 }}>Select a degree</span>
            )
          }
          onChange={(e) => {
            handleEducation(e, index);
            removeErrorMessage(`educations[${index}].degree`);
          }}
          variant="outlined"
          color={"primary"}
          sx={{
            height: "50px",
            background: "none",
            "& .MuiSelect-select": {
              height: "32px",
            },
          }}
        >
          {degreeOptions.map((deg) => (
            <MenuItem key={deg} value={deg}>
              {deg}
            </MenuItem>
          ))}
        </Select>
        {errors[`educations[${index}].degree`] && (
          <Typography fontSize={"12px"} color="error" marginTop={"3px"}>
            {errors[`educations[${index}].degree`]}
          </Typography>
        )}
      </FormControl>
      <FormControl
        sx={{
          marginBottom:
            !errors[`educations[${index}].institution`] &&
            (errors[`educations[${index}].degree`] ||
              errors[`educations[${index}].period`])
              ? "20px"
              : "",
        }}
      >
        <FormLabel htmlFor="institution">Institution</FormLabel>
        <TextField
          id={`institution-${index}`}
          type="text"
          name="institution"
          value={education.institution}
          error={!!errors[`educations[${index}].institution`]}
          helperText={errors[`educations[${index}].institution`]}
          required
          placeholder="Type an Institution"
          onChange={(e) => {
            handleEducation(e, index);
            removeErrorMessage(`educations[${index}].institution`);
          }}
          fullWidth
          variant="outlined"
          color={"primary"}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: "50px",
              boxSizing: "border-box",
            },
          }}
        />
      </FormControl>
      <FormControl
        sx={{
          marginBottom:
            !errors[`educations[${index}].period`] &&
            (errors[`educations[${index}].degree`] ||
              errors[`educations[${index}].institution`])
              ? "20px"
              : "",
        }}
      >
        <EducationDateRangePicker
          label="Period"
          value={education.period}
          onChange={(value) => {
            handleEducation(
              {
                target: {
                  name: "period",
                  value,
                },
              },
              index
            );
          }}
          error={errors[`educations[${index}].period`] ?? undefined}
          removeErrorMessage={() =>
            removeErrorMessage(`educations[${index}].period`)
          }
        />
      </FormControl>
    </Box>
  );
};
